import { elasticsearchService } from '../../elasticsearchService';
import { autoSection, type SourceDefinition } from '../helpers';
import { compactUuid } from '../../../utils/dataControllerFormat';

/**
 * Elasticsearch — retained logs. Each query OR-matches every identifier form we know (dashed and
 * compact UUID, username) because the ingestion pipelines were never consistent about which they
 * store. A term on a field an index does not have simply matches nothing.
 */

interface EsHit<T> {
  _id: string;
  _index: string;
  _source: T;
}
interface EsResponse<T> {
  hits: { total: { value: number } | number; hits: EsHit<T>[] };
}

const PAGE = 5000;

const totalOf = (r: EsResponse<unknown>): number => (typeof r.hits.total === 'number' ? r.hits.total : r.hits.total?.value ?? r.hits.hits.length);

async function esSearch<T>(index: string, should: object[], sortField: string, size = PAGE): Promise<{ rows: (T & { _id: string })[]; total: number }> {
  const response = await elasticsearchService.searchIndex<EsResponse<T>>(index, {
    size,
    sort: [{ [sortField]: { order: 'desc' } }],
    track_total_hits: true,
    query: { bool: { should, minimum_should_match: 1 } },
  });
  return { rows: response.hits.hits.map((h) => ({ _id: h._id, ...h._source })), total: totalOf(response) };
}

const uuidTerms = (field: string, uuid: string) => [{ term: { [field]: uuid } }, { term: { [field]: compactUuid(uuid) } }, { term: { [`${field}.keyword`]: uuid } }, { term: { [`${field}.keyword`]: compactUuid(uuid) } }];
const nameTerms = (field: string, names: string[]) => (names.length ? [{ terms: { [`${field}.keyword`]: names } }, { terms: { [field]: names } }] : []);

const capNote = (total: number, shown: number, index: string) => (total > shown ? [`${index} holds ${total.toLocaleString('en-AU')} matching documents; the ${shown.toLocaleString('en-AU')} most recent are included here and in the JSON. Re-run with a date filter in the log viewer for the remainder.`] : undefined);

const names = (ctx: { subject: { username: string | null; aliases: string[] } }) => Array.from(new Set([ctx.subject.username, ...ctx.subject.aliases].filter((n): n is string => !!n)));

export const esChatSource: SourceDefinition = {
  id: 'es.chat',
  title: 'Chat messages',
  system: 'Elasticsearch (chat_messages)',
  category: 'logs',
  phase: 2,
  description: 'Chat messages the player sent on any server, and whispers sent to them, as retained in the chat log index (server, channel, message type, timestamp and full text).',
  run: async (ctx) => {
    const should = [...uuidTerms('uuid', ctx.uuid), ...uuidTerms('to_uuid', ctx.uuid), ...nameTerms('user', names(ctx)), ...nameTerms('to_user', names(ctx))];
    const { rows, total } = await esSearch<Record<string, unknown>>('chat_messages', should, 'timestamp');
    return {
      sections: rows.length ? [autoSection('Messages', rows, { as: 'table', total, first: ['timestamp', 'server_fqdn', 'message_type', 'user', 'to_user', 'message_text'], omit: ['_id', 'channel_id', 'guild_id', 'uuid', 'to_uuid'], formats: { timestamp: 'datetime-iso' } })] : [],
      recordCount: rows.length,
      notes: capNote(total, rows.length, 'chat_messages'),
    };
  },
};

export const esCommandsSource: SourceDefinition = {
  id: 'es.commands',
  title: 'Command history',
  system: 'Elasticsearch (cmd_logs_v1)',
  category: 'logs',
  phase: 2,
  description: 'Commands the player executed on any server, with cluster, server, timestamp and (where captured) their in-world position at the time.',
  run: async (ctx) => {
    const should = [...uuidTerms('fromPlayerUuid', ctx.uuid), ...nameTerms('fromPlayerName', names(ctx))];
    const { rows, total } = await esSearch<Record<string, unknown>>('cmd_logs_v1', should, 'ts_ingested');
    const flat = rows.map((r) => {
      const pos = r.playerPos as { dimension?: string; x?: number; y?: number; z?: number } | undefined;
      return { ...r, position: pos ? `${pos.dimension ?? ''} ${pos.x ?? ''}, ${pos.y ?? ''}, ${pos.z ?? ''}`.trim() : null };
    });
    return {
      sections: flat.length ? [autoSection('Commands', flat, { as: 'table', total, first: ['ts_ingested', 'clusterId', 'FQDN', 'command', 'position'], omit: ['_id', 'raw', 'playerPos', 'fromPlayerUuid', 'fromPlayerName', 'version', 'type', 'serverId'], formats: { ts_ingested: 'datetime-iso', position: 'mono' } })] : [],
      recordCount: flat.length,
      notes: capNote(total, flat.length, 'cmd_logs_v1'),
    };
  },
};

export const esSpawnsSource: SourceDefinition = {
  id: 'es.spawns',
  title: 'Item & creative spawn log',
  system: 'Elasticsearch (player_spawns)',
  category: 'logs',
  phase: 2,
  description: 'Item spawns and creative-mode item grants recorded against the player, with item, quantity, cluster and whitelist state.',
  run: async (ctx) => {
    const { rows, total } = await esSearch<Record<string, unknown>>('player_spawns', uuidTerms('player_uuid', ctx.uuid), '@timestamp');
    return {
      sections: rows.length ? [autoSection('Spawns', rows, { as: 'table', total, first: ['@timestamp', 'location', 'log_type', 'item_id', 'quantity', 'is_whitelisted'], omit: ['_id', 'player_uuid'], formats: { '@timestamp': 'datetime-iso', is_whitelisted: 'bool', quantity: 'number' } })] : [],
      recordCount: rows.length,
      notes: capNote(total, rows.length, 'player_spawns'),
    };
  },
};

export const esCompensationSource: SourceDefinition = {
  id: 'es.compensation',
  title: 'Compensation requests',
  system: 'Elasticsearch (compensation_logs)',
  category: 'moderation',
  phase: 2,
  description: 'Item compensation requests the player made, including their own written reason, the staff decision and the staff member\'s reasoning.',
  run: async (ctx) => {
    const { rows, total } = await esSearch<Record<string, unknown>>('compensation_logs', uuidTerms('player_uuid', ctx.uuid), 'timestamp');
    return {
      sections: rows.length ? [autoSection('Requests', rows, { as: 'records', total, omit: ['_id', 'player_uuid'], formats: { timestamp: 'datetime-iso', staff_id: 'mono', request_id: 'mono', items: 'json' } })] : [],
      recordCount: rows.length,
      notes: capNote(total, rows.length, 'compensation_logs'),
    };
  },
};

export const esShopChangelogSource: SourceDefinition = {
  id: 'es.shop-changelog',
  title: 'Shop editor changelog',
  system: 'Elasticsearch (quasar-shopdata-changelog)',
  category: 'logs',
  phase: 2,
  description: 'Shop-configuration edits the player made in the Quasar shop editor. Each entry records the editing session token, IP address and browser user-agent.',
  run: async (ctx) => {
    const ids = [ctx.uuid, compactUuid(ctx.uuid), ...(ctx.subject.discordId ? [ctx.subject.discordId] : [])];
    const should = [{ terms: { userId: ids } }, { terms: { 'userId.keyword': ids } }];
    const { rows, total } = await esSearch<Record<string, unknown>>('quasar-shopdata-changelog', should, '@timestamp', 2000);
    for (const r of rows) ctx.addIps([typeof r.ip === 'string' ? r.ip : null]);
    return {
      sections: rows.length ? [autoSection('Edits', rows, { as: 'records', total, omit: ['_id'], formats: { '@timestamp': 'datetime-iso', ip: 'mono', sessionJti: 'mono', userId: 'mono', changelog: 'json', totals: 'json', affectedPageIds: 'json' } })] : [],
      recordCount: rows.length,
      notes: capNote(total, rows.length, 'quasar-shopdata-changelog'),
    };
  },
};
